import { useCallback, useMemo } from 'react';

import {
  useUsers,
  useRoles,
  useCompanies,
  useDeletedUsers,
  useFormattedUser,
} from '@adsk/alloy-provider';
import {
  DropdownProps,
  GetFilteredItems,
  VirtualizedMenu,
} from '@adsk/alloy-react-dropdown';
import { useLocale } from '@adsk/alloy-react-locale';

import {
  ASSIGNEE_PICKER_COMPANY,
  ASSIGNEE_PICKER_ROLE,
  ASSIGNEE_PICKER_USER,
  DEFAULT_OPTIONS_ORDER,
  DEFAULT_USERS_STATUS_FILTER,
  ASSIGNEE_PICKER_UNASSIGNED,
  AUTODESK_ID_VALUE_KEY,
  MEMBER_GROUP_ID_VALUE_KEY,
  DEFAULT_STATUS_FILTERS,
  LOADING_STYLE,
} from '../consts';
import {
  transformCompanies,
  transformUsers,
  transformRoles,
} from '../helpers/formatters';
import { sortUsersAlphabetically } from '../helpers/utils';
import {
  AssigneeGroup,
  GroupedItem,
  UseAssigneePickerProps,
  ItemWithGroup,
} from '../types';

type ReturnType = {
  dropdownProps: Pick<
    DropdownProps<ItemWithGroup>,
    'isLoading' | 'groupedItems' | 'renderMenu'
  > & {
    getFilteredItems: GetFilteredItems<ItemWithGroup>;
  };
};

/**
 * A Dropdown component to multiselect items from a dropdown that
 * contains Users, Roles and Companies grouped by category
 */
export const useAssigneePicker = ({
  companyOptionValue = MEMBER_GROUP_ID_VALUE_KEY,
  roleOptionValue = MEMBER_GROUP_ID_VALUE_KEY,
  userOptionValue = AUTODESK_ID_VALUE_KEY,
  companyOptionLabel,
  roleOptionLabel,
  userOptionLabel,
  userOptionSubtextRenderer,
  optionsOrder = DEFAULT_OPTIONS_ORDER,
  statusFilters = DEFAULT_STATUS_FILTERS,
  optionsFilter = {},
  additionalCompanyDataFields,
  additionalUserDataFields,
  additionalRoleDataFields,
  customUsers,
  customRoles,
  customCompanies,
  customUnassigned,
  postFormatters,
  hideDeletedEntitiesInMenu = { users: true },
}: UseAssigneePickerProps | undefined = {}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  const UNASSIGNED_ITEM =
    customUnassigned ||
    ({
      label: T.translate('UNASSIGNED') as string,
      items: [
        {
          label: T.translate('UNASSIGNED') as string,
          value: 'null',
          group: 'unassigned',
        },
      ],
      group: ASSIGNEE_PICKER_UNASSIGNED,
    } satisfies GroupedItem);

  const alloyUsers = useUsers();
  const users = customUsers || alloyUsers;

  const { users: statusFiltersUsers = DEFAULT_USERS_STATUS_FILTER } =
    statusFilters;

  const includeDeletedUsers = useMemo(
    () => statusFiltersUsers.includes('deleted') && !customUsers,
    [customUsers, statusFiltersUsers],
  );
  const alloyDeletedUsers = useDeletedUsers({ disable: !includeDeletedUsers });

  const alloyRoles = useRoles();
  const roles = customRoles || alloyRoles;

  const alloyCompanies = useCompanies();
  const companies = customCompanies || alloyCompanies;

  const isLoading = (
    includeDeletedUsers
      ? [users, roles, companies, alloyDeletedUsers]
      : [users, roles, companies]
  ).some((entity) => entity?.loading);

  const { formatDisplayUser } = useFormattedUser();
  const companyOptionsFilter = optionsFilter[ASSIGNEE_PICKER_COMPANY];
  const roleOptionsFilter = optionsFilter[ASSIGNEE_PICKER_ROLE];
  const userOptionsFilter = optionsFilter[ASSIGNEE_PICKER_USER];

  const groupedItems = useMemo(() => {
    const [companyIdToNameMap, companyOptions] = transformCompanies(
      companies.data,
      companyOptionValue,
      T,
      statusFilters.companies,
      companyOptionsFilter,
      additionalCompanyDataFields,
      postFormatters?.postTransformCompanies,
    );

    const [rolesMemberCount, userOptions] = transformUsers(
      includeDeletedUsers
        ? sortUsersAlphabetically([...users.data, ...alloyDeletedUsers.data])
        : users.data,
      userOptionValue,
      formatDisplayUser,
      statusFiltersUsers,
      userOptionsFilter,
      additionalUserDataFields,
      companyIdToNameMap,
      userOptionSubtextRenderer,
      postFormatters?.postTransformUsers,
      includeDeletedUsers && hideDeletedEntitiesInMenu.users,
    );

    const roleItems = transformRoles(
      roles.data,
      roleOptionValue,
      T,
      statusFilters.roles,
      roleOptionsFilter,
      additionalRoleDataFields,
      rolesMemberCount,
      postFormatters?.postTransformRoles,
    );

    const allOptions: { [key in AssigneeGroup]: GroupedItem[] } = {
      unassigned: [UNASSIGNED_ITEM],
      company: [
        {
          label: companyOptionLabel || (T.translate('COMPANIES') as string),
          items: companyOptions,
          group: ASSIGNEE_PICKER_COMPANY,
        },
      ],
      role: [
        {
          label: roleOptionLabel || (T.translate('ROLES') as string),
          items: roleItems,
          group: ASSIGNEE_PICKER_ROLE,
        },
      ],
      user: [
        {
          label: userOptionLabel || (T.translate('MEMBERS') as string),
          items: userOptions,
          group: ASSIGNEE_PICKER_USER,
        },
      ],
    };

    return isLoading
      ? []
      : optionsOrder.reduce<GroupedItem[]>(
          (acc, category) => [...acc, ...allOptions[category]],
          [],
        );
  }, [
    companies.data,
    companyOptionValue,
    T,
    statusFilters.companies,
    statusFilters.roles,
    companyOptionsFilter,
    additionalCompanyDataFields,
    postFormatters?.postTransformCompanies,
    postFormatters?.postTransformUsers,
    postFormatters?.postTransformRoles,
    includeDeletedUsers,
    users.data,
    alloyDeletedUsers.data,
    userOptionValue,
    formatDisplayUser,
    statusFiltersUsers,
    userOptionsFilter,
    additionalUserDataFields,
    userOptionSubtextRenderer,
    hideDeletedEntitiesInMenu.users,
    roles.data,
    roleOptionValue,
    roleOptionsFilter,
    additionalRoleDataFields,
    UNASSIGNED_ITEM,
    companyOptionLabel,
    roleOptionLabel,
    userOptionLabel,
    isLoading,
    optionsOrder,
  ]);

  const getFilteredItems = useCallback(
    ({
      allItems,
      inputValue = '',
    }: {
      allItems: ItemWithGroup[];
      inputValue?: string;
    }) => {
      const search = inputValue.trim().toLowerCase();

      if (!search) return allItems;

      return allItems.filter((item) => {
        const { label, subtext } = item;

        return (
          (label && label.toString().toLowerCase().indexOf(search) >= 0) ||
          (subtext && subtext.toString().toLowerCase().indexOf(search) >= 0)
        );
      });
    },
    [],
  );

  return {
    dropdownProps: {
      isLoading,
      groupedItems,
      getFilteredItems,
      renderMenu: (menuProps, virtualProps) =>
        VirtualizedMenu.render(
          {
            ...menuProps,
            isLoading,
            renderFooter: () => null,
            style: {
              ...menuProps.style,
              ...(isLoading && LOADING_STYLE),
            },
          },
          virtualProps,
        ),
    },
  } satisfies ReturnType;
};
